<template>
  <div class="table-wrapper">
    <!--
      keep-alive 缓存三种表格模式（标准 / 透视表 / 透视图）的组件实例。
      左侧切换 tab 时，被隐藏的组件不会被销毁，其已加载的数据与视图状态
      保留在内存中，再次切回时可以瞬间渲染，无需重新拉取数据。
      注意：三个组件各自绑定独立的 config，避免共用同一份 config 导致
      缓存实例在重新激活时被错误 prop 覆盖。
    -->
    <keep-alive>
      <template v-if="normalConfig && tableMode === 'normal'">
        <a-alert
          v-if="filter_id_key"
          type="warning"
          closable
          show-icon
          @close="onClearFilterIdkey"
        >
          <template #message>
            <div class="filter-message">
              <span
                v-for="item in filterList"
                :key="item.label"
              >
                {{ item.label }}:
                <a-tag>{{ item.value }}</a-tag>
              </span>
              <span>
                值字段:
                <a-tag>{{ matchValueLabel }}</a-tag>
              </span>
            </div>
            <div>
              当前数据已通过透视表定位，此功能为临时过滤，不会被视图记录。关闭可恢复原始数据。
            </div>
          </template>
        </a-alert>

        <tablePro
          :config="normalConfig"
          ref="tableProRef"
          :eventBus="eventBus"
        >
          <template #header_bar_before_title>
            <div v-if="pageConfig.isView">
              <div
                class="toggle-button"
                @click="toggleOpen"
              >
                <MenuFoldOutlined v-if="leftOpen" />
                <MenuUnfoldOutlined v-else />
              </div>
            </div>
          </template>
        </tablePro>
      </template>
      <pivotTable
        v-else-if="pivotConfig && tableMode === 'pivot'"
        :config="pivotConfig"
        ref="pivotTableRef"
        :eventBus="eventBus"
        @filter-for-id-key-start="handleFilterForIdKeyStart"
        @filterForIdKey="handleFilterForIdKey"
      >
        <template #header_bar_before_title>
          <div v-if="pageConfig.isView">
            <div
              class="toggle-button"
              @click="toggleOpen"
            >
              <MenuFoldOutlined v-if="leftOpen" />
              <MenuUnfoldOutlined v-else />
            </div>
          </div>
        </template>
      </pivotTable>
      <pivotCharts
        v-else-if="pivotChartsConfig && tableMode === 'pivot_charts'"
        :config="pivotChartsConfig"
        ref="pivotChartsRef"
        :eventBus="eventBus"
      >
        <template #header_bar_before_title>
          <div v-if="pageConfig.isView">
            <div
              class="toggle-button"
              @click="toggleOpen"
            >
              <MenuFoldOutlined v-if="leftOpen" />
              <MenuUnfoldOutlined v-else />
            </div>
          </div>
        </template>
      </pivotCharts>
    </keep-alive>

    <!-- 透视表双击定位时的全局 loading 遮罩：接口可能耗时较久，覆盖整页防止误操作 -->
    <FilterLoading :visible="filterLoading" />
  </div>
</template>
<script setup lang="ts">
// ============================================================================
// sql_db_view 右侧容器（蓝本：remotePages/v2/rightTable.vue，按 §4.1 裁剪）
// 与蓝本的差异：
//   ① 去掉 btnAdd / 增删改（只读页）；
//   ② 去掉 AI 助手页面能力注册（ai-view-helper / ai-view-ability-schemas / 快速操作）；
//   ③ ★ v24（决策 24）：去掉全部按钮权限校验 —— 不 CHECK_AUTH(VIEW_SYSTEM_EDIT)、
//      无 is_view_system_edit_auth，保存视图的系统视图操作一律放行；
//   ④ ★ v10/v23：pivot / field_search_config URL 显式声明（pageConfig 已由 pageConfig.ts
//      按 flag 拼好），不做 `_list` → `_pivot_table_rust` 字符串替换派生；
//   ⑤ v20：只支持高级搜索（is_show_advantage 恒 true）。
// ============================================================================
import tablePro from 'liyu-pc-base/components/tablePro/tablePro.vue';
import pivotTable from 'liyu-pc-base/components/tablePro/pivotTable.vue';
import pivotCharts from 'liyu-pc-base/components/tablePro/pivotCharts.vue';
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import type { SqlViewPageConfig } from './typing';
import type {
  SearchGroupItem,
  Table,
  SearchConfig,
  RefreshType,
  RowSelectionType,
} from 'liyu-pc-base/components/tablePro/typing';
import FilterLoading from './FilterLoading.vue';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons-vue';
import { useRoute } from 'vue-router';
import { genID } from 'liyu-pc-base/utils/function';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import { useI18n } from 'vue-i18n';
import { message } from 'ant-design-vue';
import { buildRuntimeUrls } from './pageConfig';

// 三种表格模式各自独立的 config：
// keep-alive 缓存隐藏的组件实例，若共用同一个 config，重新激活时 prop 会
// 被其它模式的 config 覆盖。拆分为三个后才能保证缓存实例数据不被污染。
const normalConfig = ref<Table>(null);
const pivotConfig = ref<Table>(null);
const pivotChartsConfig = ref<Table>(null);

interface Props {
  eventBus: any;
  pageConfig: SqlViewPageConfig;
  modalType: string;
  tableMode?: 'normal' | 'pivot' | 'pivot_charts';
  setting_drawer_width: number;
}
const props = withDefaults(defineProps<Props>(), {
  eventBus: null,
  pageConfig: null,
  tableMode: 'normal',
});
const emit = defineEmits(['changeMode']);
const userStore = useUserStore();
const { t } = useI18n();
const route = useRoute();
const tableProRef = ref(null);
const pivotTableRef = ref(null);
const pivotChartsRef = ref(null);
const filter_id_key = ref('');
const filterList = ref<{ label: string; value: string }[]>([]);
const matchValueLabel = ref('');
// 透视表双击定位时的全局 loading 遮罩：filterForIdKeyStart 打开，filterForIdKey 关闭
const filterLoading = ref(false);
const activeTableRef = computed(() => {
  if (props.tableMode === 'pivot') return pivotTableRef.value;
  if (props.tableMode === 'pivot_charts') return pivotChartsRef.value;
  return tableProRef.value;
});
const leftOpen = defineModel('leftOpen', {
  default: true,
});

// 三种表格模式在挂载时一次性构建各自的 config。
// 切换 tableMode 不再销毁/重建组件，而是由 keep-alive 缓存隐藏实例，
// 已加载的数据随实例保留，切回时即时显示，无需重新拉取。
onMounted(() => {
  normalConfig.value = getConfig('normal');
  pivotConfig.value = getConfig('pivot');
  pivotChartsConfig.value = getConfig('pivot_charts');
});

// 切换左侧 tab 时同步更新 URL hash：
// keep-alive 下被隐藏的组件实例被缓存，其 currentView 不再变化，
// 左侧视图组件（viewsConfig）的 hash 更新 watch 不会再触发，
// 因此这里在每次切换模式时显式把「模式前缀 + 当前视图 uid」写入 hash，
// 保证刷新页面后能根据 hash 前缀恢复正确的表格模式。
watch(
  () => props.tableMode,
  async newMode => {
    await nextTick();
    const prefix =
      newMode === 'pivot' ? 'pivot:' : newMode === 'pivot_charts' ? 'pivotcharts:' : '';
    const uid = activeTableRef.value?.currentView?.uid || '';
    window.location.hash = `${prefix}${uid}`;
  },
);
const toggleOpen = () => {
  leftOpen.value = !leftOpen.value;
};

// 展平高级搜索分组（v20：只支持高级搜索，无普通搜索分支）
const getSearchConfigList = (): SearchConfig[] => {
  const search_config_list: SearchConfig[] = [];
  for (const group of props.pageConfig.base_search_condition_config_list || []) {
    for (const item of group.group || []) {
      search_config_list.push({ ...item, group_name: group.name });
    }
  }
  return search_config_list;
};
const getRouteParams = () => {
  let query_json = {};
  if (route.query?.query_json) {
    query_json = JSON.parse(route.query?.query_json as any);
  }
  const routeQueryParams = JSON.parse(JSON.stringify({ ...route.query, ...query_json }));
  const del_keys = [
    't',
    'query_json',
    'is_router',
    'is_global',
    'is_fuzzy_search',
    'customerTitle',
  ];
  for (const key of del_keys) {
    delete routeQueryParams[key];
  }
  if (routeQueryParams?.ext_search_keys) {
    let ext_search_keys = routeQueryParams.ext_search_keys;
    ext_search_keys = ext_search_keys.split(',').map(item => item.trim());
    const _ext_search = {};
    ext_search_keys.forEach((key: string) => {
      _ext_search[key] = routeQueryParams[key];
    });
    return _ext_search;
  } else {
    return routeQueryParams;
  }
};
const getConfig = (mode: 'normal' | 'pivot' | 'pivot_charts'): any => {
  const isNormal = mode === 'normal';
  const isPivot = mode === 'pivot';
  const isPivotCharts = mode === 'pivot_charts';
  let keyPrefix = '';
  let hashPrefix = '';
  if (isPivot) {
    keyPrefix = 'pivot-';
    hashPrefix = 'pivot:';
  } else if (isPivotCharts) {
    keyPrefix = 'pivotcharts-';
    hashPrefix = 'pivotcharts:';
  }
  // ★ v20：只支持高级搜索
  const search_config_list = getSearchConfigList();
  const default_advanced_search_multigroups: SearchGroupItem[] = [];

  for (const group of props.pageConfig.default_search_condition_config_list || []) {
    const defaultItem: SearchGroupItem = {
      id: genID(),
      conditions: [],
      viewSearchConfig: {},
    };
    for (const item of search_config_list) {
      defaultItem.viewSearchConfig[item.field_name] = {
        checked: false,
        order: 9999,
      };
    }
    let order = 0;
    for (const item of group || []) {
      defaultItem.conditions.push(item);
      defaultItem.viewSearchConfig[item.field_name] = {
        checked: true,
        order: order,
      };
      order++;
    }
    default_advanced_search_multigroups.push(defaultItem);
  }
  let hash = '';
  if (window.location.hash) {
    hash = window.location.hash.substring(1); // 去掉 # 符号
    // Filter hash by mode: pivot hash starts with 'pivot:', pivotcharts hash starts with 'pivotcharts:'
    if (hashPrefix) {
      if (hash.startsWith(hashPrefix)) {
        hash = hash.substring(hashPrefix.length);
      } else {
        hash = '';
      }
    } else {
      // normal mode: ignore pivot: and pivotcharts: hashes
      if (hash.startsWith('pivot:') || hash.startsWith('pivotcharts:')) {
        hash = '';
      }
    }
  }
  let base_conditions = null;
  // ★ v27：query_params 不在白名单，PageConfig 不携带；仅透视定位临时过滤会写入 filter_id_key
  let query_params: any = {};
  // ★ v10/v23：路由参数统一按高级搜索条件（field_name/operator/value）下发
  const route_base_conditions = [];
  Object.entries(getRouteParams()).forEach(([key, value]) => {
    if (!Array.isArray(value)) {
      value = [value];
    }
    route_base_conditions.push({
      field_name: key,
      operator: 'in',
      value: value,
      format: 'input',
      class_name: 'str',
    });
  });
  if (
    Array.isArray(props.pageConfig.advanced_base_conditions) &&
    props.pageConfig.advanced_base_conditions.length > 0 &&
    route_base_conditions.length > 0
  ) {
    base_conditions = [
      { op: 'and', children: props.pageConfig.advanced_base_conditions },
      { op: 'and', children: route_base_conditions },
    ];
  } else if (props.pageConfig.advanced_base_conditions?.length > 0) {
    base_conditions = props.pageConfig.advanced_base_conditions;
  } else if (route_base_conditions.length > 0) {
    base_conditions = route_base_conditions;
  }
  if (filter_id_key.value) {
    query_params.filter_id_key = filter_id_key.value;
  }
  // ★ v29.14：width 为 null 必须剥掉——@surely-vue/table 列宽计算只防 undefined/number，
  //   null 会直接进 o.match(/^(.*)px$/) 抛 TypeError（页面白屏级报错，真机已复现）
  const columns = (props.pageConfig.baseColumns || [])
    .filter(item => item.dataIndex !== 'action')
    .map(item => {
      if (item.width == null) {
        const { width: _ignored, ...rest } = item;
        return rest;
      }
      return item;
    });
  if (userStore.HAS_ACTION_BTN(props.pageConfig)) {
    columns.push({
      title: t('操作'),
      width: 50,
      dataIndex: 'action',
      checked: true,
      fixed: 'right',
      cellComponent: 'action',
    });
  }
  // ★ v10/v23/v27：URL 全部由 buildRuntimeUrls(view_flag) 显式拼装（pageConfig.ts 导出），
  //   不做 queryData.replace('_list', '_pivot_table_rust') 式字符串替换派生
  const urls = buildRuntimeUrls(props.modalType.split('-')[1]);
  return {
    key: `${keyPrefix}sql_db_view-${props.modalType}`,
    title: (route.query?.customTitle as string) || (route.meta.title as string) || '',
    tableSize: 'small',
    search_display_type: 'hide',
    search_type: 'advanced',
    is_selection: props.pageConfig.is_selection,
    is_pagination: props.pageConfig.is_pagination,
    list_url: urls.queryData,
    setting_mode: 'drawer',
    setting_drawer_width: props.setting_drawer_width,
    pivot_charts_url: urls.pivotChartsUrl,
    pivot_table_url: urls.pivotTableUrl,
    pivot_table_max_records: props.pageConfig.pivot_table_max_records,
    search_config: search_config_list,
    columns: columns,
    // ★ v29.24（用户定案）/ ★ v29.40 三态：tablePro 用的 S-Table 必须有唯一行键。SQL 视图的
    //   行身份由后端 get_page_config 下发 —— 恰好 1 个主键列 → 该列名；0 个或 ≥2 个主键列 →
    //   '_id'（列表接口为每行注入的稳定隐含主键：0 列是整行内容 hash，≥2 列是各主键值用 '|' 拼接，
    //   翻页/排序/裁剪列后都不变）。前端只透传，不做分支。
    rowKey: props.pageConfig.rowKey || 'id',
    show_current_view: true,
    enable_custom_filter: true,
    query_field_search_config_url: urls.fieldSearchConfigUrl,
    enable_advanced_search_multigroups: true,
    default_advanced_search_multigroups: default_advanced_search_multigroups,
    condition_group_config_list: props.pageConfig.condition_group_config_list || [],
    query_group_url: urls.queryConditionGroupData,
    refreshType: props.pageConfig.refresh_type as RefreshType,
    pageSize: props.pageConfig.pageSize,
    pageSizeOptions: props.pageConfig.page_size_options,
    view_uid: hash,
    base_conditions: base_conditions,
    query_params: query_params,
    btn_list: props.pageConfig.btn_list || [],
    head_btn_list: props.pageConfig.head_btn_list || [],
    batch_update_list: [],
    required_condition: props.pageConfig.required_condition,
    count_url: isNormal ? urls.countData : undefined,
    customRow: props.pageConfig.customRow || undefined,
    customHeaderCell: props.pageConfig.customHeaderCell || undefined,
    customCell: props.pageConfig.customCell || undefined,
    rowSelectionType: (props.pageConfig.rowSelection?.type || 'checkbox') as RowSelectionType,
    rowSelectionCheckStrictly: props.pageConfig.rowSelection?.checkStrictly || false,
    hide_index_column: false,
    // ★ v27：get_db_alias 不在白名单（数据源由后端 DbDataSource 决定），不传
    disabledShowSearchGroupName: true,
    cellComponentExt: {
      // ★ v24：无 titleList 权限位；api_fields（lybase 编辑表单字典）不在白名单，一期不提供
      titleList: null,
      api_fields: null,
      modalType: props.modalType,
      app_flag: 'db_view',
      getData: urls.getData,
      pageConfig: props.pageConfig,
    },
  };
};
const handleFilterForIdKeyStart = () => {
  // 透视表双击定位：开始向后端请求（接口可能耗时较久），先展示全局 loading 遮罩
  filterLoading.value = true;
};
const handleFilterForIdKey = (params: {
  filter_id_key: string;
  filterList: { label: string; value: string }[];
  matchValueLabel: string;
}) => {
  // 后端接口已完成（成功或失败），先关闭全局 loading 遮罩，再进行后续 tab 页跳转
  filterLoading.value = false;
  if (params) {
    filter_id_key.value = params.filter_id_key;
    filterList.value = params.filterList;
    matchValueLabel.value = params.matchValueLabel;
    normalConfig.value = getConfig('normal');
    emit('changeMode', 'normal');
    nextTick(() => {
      tableProRef.value.currentView = { id: 'no-view' };
      tableProRef.value.setViewData(null);
    });
  } else {
    // 定位失败（pivotTable 在接口异常时回传 null），给出明确反馈
    message.error('数据定位失败，请重试');
  }
};
const onClearFilterIdkey = () => {
  filter_id_key.value = '';
  normalConfig.value = getConfig('normal');
};

// defineExpose 在 setup 末尾调用:它同步读取引用的变量,必须在所有 const 定义之后,
// 否则会触发 TDZ(Cannot access 'X' before initialization)。
defineExpose({
  tableProRef,
  pivotTableRef,
  pivotChartsRef,
  activeTableRef,
});
</script>

<style scoped lang="less">
.table-wrapper {
  flex: 1;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toggle-button {
  cursor: pointer;
  height: 46px;
  line-height: 33px;
  font-size: 20px;
  &:hover {
    color: #1890ff;
    filter: drop-shadow(2px 2px 2px #bddfff);
  }
}
.filter-message {
  display: flex;
  gap: 14px;
}
</style>
